import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared/src/propertyGrid/consts';
import { Block, Typography } from '@aloft/toolkit-sui';

import React from 'react';

import { selectHasArvOption } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { AdjustmentField } from '../fields/AdjustmentField.tsx';
import { EditableDateField } from '../fields/EditableDateField.tsx';
import { EditableEnumField } from '../fields/EditableEnumField.tsx';
import { EditableField } from '../fields/EditableField.tsx';
import { LabelField } from '../fields/LabelField.tsx';
import { ReadOnlyField } from '../fields/ReadOnlyField.tsx';
import { SimpleEditableField } from '../fields/SimpleEditableField.tsx';
import { readOnlyFieldTypographyProps } from '../fields/consts.ts';
import { AgeWithYearBuiltField } from '../fields/customFields.tsx/AgeWithYearBuiltField.tsx';
import { ContractDateField } from '../fields/customFields.tsx/ContractDateField.tsx';
import { DataSourceValueField } from '../fields/customFields.tsx/DataSourceValueField.tsx';
import { SoldDateField } from '../fields/customFields.tsx/SoldDateField.tsx';
import { SubjectTotalCostToCureField } from '../fields/customFields.tsx/SubjectTotalCostToCureField.tsx';
import { PropertyGridFieldProps } from '../fields/types.ts';
import { PropertyGridField, PropertyGridSectionProps } from '../types.ts';

type PropertyGridRowProps = Pick<
  PropertyGridSectionProps,
  | 'columnIndex'
  | 'fieldTypes'
  | 'gridType'
  | 'hasSeparateArvValue'
  | 'control'
  | 'getValues'
  | 'setValue'
> & {
  field: PropertyGridField;
  sectionIsNone?: boolean;
  arvSectionIsNone?: boolean;
};

export const PropertyGridRow = ({
  columnIndex,
  fieldTypes,
  gridType,
  field,
  sectionIsNone = false,
  hasSeparateArvValue = false,
  arvSectionIsNone = false,
  control,
  getValues,
  setValue,
}: PropertyGridRowProps) => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const { fieldName } = field;

  const columnType = getValues(`columns.${columnIndex}.type`);
  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const showAivComps = !hasArvOption || activeTab === 'all' || activeTab === 'aiv';
  const showArvComps = !hasArvOption || activeTab === 'all' || activeTab === 'arv';

  const arvIsEnabled = hasArvOption && showArvComps;
  const showSeparateArvField =
    columnType === 'property' && isSubject && arvIsEnabled && hasSeparateArvValue;

  return (
    <>
      {fieldTypes.map((fieldType) => {
        let FieldComponent: React.ComponentType<PropertyGridFieldProps> | undefined;
        let isArv = false;
        let colSpan = 1;

        // Special case for age fields - use custom component with year built
        const isAgeField = fieldName === 'editableData.improvements.age';
        // Special case for date fields - use date picker component
        const isDateField =
          fieldName === 'editableData.transaction.contractDate' ||
          fieldName === 'editableData.transaction.saleDate' ||
          fieldName === 'editableData.transaction.expirationDate' ||
          fieldName === 'editableData.transaction.withdrawalDate';

        const isSoldDateField = fieldName === 'editableData.transaction.saleDate';
        const isContractDateField = fieldName === 'editableData.transaction.contractDate';
        const isPropertyTypeField = fieldName === 'editableData.improvements.propertyType';

        const isAdjustmentsGrid = gridType === 'AdjustmentsGrid';
        const isOtherAdjustmentsField = isAdjustmentsGrid && field.manualAdjustmentKey === 'other';
        const isTotalCostToCureAdjustmentField =
          isAdjustmentsGrid && field.manualAdjustmentKey === 'totalCostToCure';

        switch (fieldType) {
          case 'dataSourceValue':
            FieldComponent = DataSourceValueField;
            break;
          // @ts-expect-error - Fallthrough intended
          case 'editableArvValue':
            if (!showSeparateArvField) break;

            if (isSubject && (isAgeField || isPropertyTypeField) && activeTab !== 'arv') {
              break;
            }
            isArv = true;
          // eslint-disable-next-line no-fallthrough
          case 'editableValue': {
            const shouldHideSubjectEditableValue =
              isSubject && !showAivComps && !isArv && hasSeparateArvValue;
            if (shouldHideSubjectEditableValue) break;
            if (isSubject && !isArv && arvIsEnabled && !hasSeparateArvValue) {
              colSpan = activeTab === 'all' ? 2 : 1;
            }

            if ((sectionIsNone && !isArv) || (arvSectionIsNone && isArv)) {
              return (
                <Block
                  key={`${columnIndex}-${fieldName}-${fieldType}-none`}
                  gridColumn={`span ${colSpan}`}
                >
                  <Typography {...readOnlyFieldTypographyProps}>None</Typography>
                </Block>
              );
            }

            if (isSubject && hasArvOption && (isAgeField || isPropertyTypeField)) {
              colSpan = activeTab === 'all' ? 2 : 1;
            }

            if (isAgeField) FieldComponent = AgeWithYearBuiltField;
            else if (isSoldDateField) FieldComponent = SoldDateField;
            else if (isContractDateField) FieldComponent = ContractDateField;
            else if (isDateField) FieldComponent = EditableDateField;
            else if (field.options) FieldComponent = EditableEnumField;
            else FieldComponent = EditableField;
            break;
          }
          // @ts-expect-error - Fallthrough intended
          case 'readOnlyArvValue': {
            if (
              (!isAdjustmentsGrid && !showSeparateArvField) ||
              isOtherAdjustmentsField ||
              isTotalCostToCureAdjustmentField
            ) {
              break;
            }
            isArv = true;
          }
          // eslint-disable-next-line no-fallthrough
          case 'readOnlyValue':
            if (isSubject && !isArv && arvIsEnabled && !hasSeparateArvValue) {
              colSpan = isAdjustmentsGrid ? 1 : 2;
            }

            if (isOtherAdjustmentsField || isTotalCostToCureAdjustmentField) {
              colSpan = isSubject && arvIsEnabled ? 2 : 1;
            }
            else if (isOtherAdjustmentsField) FieldComponent = SimpleEditableField;
            else if (isTotalCostToCureAdjustmentField) {
              FieldComponent = isSubject ? SubjectTotalCostToCureField : SimpleEditableField;
            } else FieldComponent = ReadOnlyField;
            break;
          case 'adjustment':
            FieldComponent = AdjustmentField;
            break;
          case 'label':
          default:
            FieldComponent = LabelField;
            break;
        }

        if (!FieldComponent) return null;

        return (
          <FieldComponent
            key={`${columnIndex}-${fieldName}-${fieldType}-field`}
            columnIndex={columnIndex}
            field={field}
            isArv={isArv}
            colSpan={colSpan}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        );
      })}
    </>
  );
};
